// Trwałość danych po stronie przeglądarki: czy śpiewnik ma szansę przeżyć, i
// czy człowiek wie, od czego to zależy.
//
// Powód istnienia tego modułu jest odczytany ze źródła, nie założony.
// `webkit.org/tracking-prevention/`, sekcja „Home Screen Web Application Domain
// Exempt From ITP" (sprawdzone 28.08.2026), mówi wprost: siedmiodniowe
// kasowanie magazynu zapisywalnego ze skryptu (IndexedDB, cache, rejestracje
// service workera) omija domenę aplikacji dodanej do ekranu głównego, a jej
// dane są trzymane odrębnie od zwykłej karty Safari.
//
// Wniosek dla produktu jest mocniejszy niż sama ciekawostka: **dodanie do
// ekranu głównego przestaje być wygodą, a staje się warunkiem trwałości**.
// Aplikacja, która o tym milczy, po tygodniu przerwy potrafi wstać pusta, a
// człowiek nie ma jak zgadnąć, dlaczego.

export type StanTrwalosci =
  /** Uruchomione z ekranu głównego, magazyn trwały: nic nie trzeba robić. */
  | { rodzaj: 'trwale' }
  /** Uruchomione z ekranu głównego, ale przeglądarka nie potwierdziła trwałości. */
  | { rodzaj: 'zEkranu' }
  /** Zwykła karta przeglądarki: tu obowiązuje kasowanie po tygodniu bez wejścia. */
  | { rodzaj: 'karta' };

/** Czy aplikacja działa jako osobne okno (ekran główny), czy w karcie.
 * Dwa sprawdzenia, bo `display-mode: standalone` jest tym standardowym, a
 * `navigator.standalone` starszym, własnym mechanizmem iOS, który bywa
 * jedynym prawdziwym na tym systemie. */
export function zEkranuGlownego(): boolean {
  const wlasneOkno = window.matchMedia?.('(display-mode: standalone)').matches === true;
  const iosStandalone = (navigator as { standalone?: boolean }).standalone === true;
  return wlasneOkno || iosStandalone;
}

/**
 * Pyta przeglądarkę o trwały magazyn i zwraca stan do pokazania człowiekowi.
 *
 * `persist()` wolno wywołać zawsze, ale odpowiedź zależy od przeglądarki i w
 * WebKicie bierze pod uwagę między innymi to, czy aplikacja jest uruchamiana z
 * ekranu głównego. Dlatego wynik trzeba ODCZYTAĆ, a nie założyć: „poprosiłem"
 * nie znaczy „dostałem", a to właśnie ta różnica decyduje, czy śpiewnik
 * przetrwa przerwę.
 *
 * Brak API nie jest błędem i nie ma prawa niczego wywrócić: wtedy zostaje sama
 * informacja o tym, skąd aplikacja jest uruchomiona.
 */
export async function sprawdzTrwalosc(): Promise<StanTrwalosci> {
  const zEkranu = zEkranuGlownego();

  try {
    const magazyn = navigator.storage;
    if (magazyn?.persisted && magazyn.persist) {
      const juzTrwale = await magazyn.persisted();
      const trwale = juzTrwale || (await magazyn.persist());
      if (trwale) return { rodzaj: 'trwale' };
    }
  } catch {
    // Cicho, bo brak albo odmowa tego API nie zmienia niczego w działaniu
    // aplikacji: zmienia tylko to, co mamy o niej powiedzieć niżej.
  }

  return zEkranu ? { rodzaj: 'zEkranu' } : { rodzaj: 'karta' };
}

/** Zdanie dla stopki menu, albo `null`, gdy nie ma nic do powiedzenia.
 * Milczenie przy stanie trwałym jest celowe: komunikat „wszystko w porządku"
 * na ekranie, którego cała obietnica brzmi „tylko to, co potrzebne", byłby
 * szumem zajmującym wiersz. */
export function zdanieOTrwalosci(stan: StanTrwalosci): string | null {
  if (stan.rodzaj === 'trwale') return null;
  if (stan.rodzaj === 'zEkranu') return 'śpiewnik zapisany na urządzeniu';
  return 'dodaj do ekranu głównego, inaczej śpiewnik zniknie po tygodniu przerwy';
}
